import { fetchQuery, graphql } from 'react-relay';
import type { Disposable, IEnvironment } from 'relay-runtime';

import type { resolveAgentSessionsCloudIdQuery } from './__generated__/resolveAgentSessionsCloudIdQuery.graphql';

type ResolveAgentSessionsCloudIdOptions = Readonly<{
	rovoActivationId: string;
	onComplete: (result: Readonly<{ cloudId?: string; error?: Error }>) => void;
}>;

const cloudIdsByEnvironment = new WeakMap<object, Map<string, string>>();

const getCloudIdCache = (environment: IEnvironment): Map<string, string> => {
	let cache = cloudIdsByEnvironment.get(environment);
	if (cache == null) {
		cache = new Map();
		cloudIdsByEnvironment.set(environment, cache);
	}
	return cache;
};

const getNonEmptyCloudIds = (
	tenantContexts:
		| readonly (Readonly<{ readonly cloudId?: string | null }> | null)[]
		| null
		| undefined,
): string[] => {
	return Array.from(
		new Set(
			tenantContexts
				?.map((tenantContext) => tenantContext?.cloudId?.trim())
				.filter((cloudId): cloudId is string => cloudId != null && cloudId.length > 0) ?? [],
		),
	);
};

/**
 * Resolves the cloud identity for an explicitly selected Rovo activation
 * without changing the suspending read-side activation resolver.
 */
export const resolveAgentSessionsCloudId = (
	environment: IEnvironment,
	{ rovoActivationId, onComplete }: ResolveAgentSessionsCloudIdOptions,
): Disposable => {
	const cache = getCloudIdCache(environment);
	const cachedCloudId = cache.get(rovoActivationId);
	if (cachedCloudId != null) {
		onComplete({ cloudId: cachedCloudId });
		return { dispose: () => {} };
	}

	let disposed = false;
	const subscription = fetchQuery<resolveAgentSessionsCloudIdQuery>(
		environment,
		graphql`
			query resolveAgentSessionsCloudIdQuery($rovoActivationId: ID!) {
				tenantContexts(activationIds: [$rovoActivationId]) {
					cloudId
				}
			}
		`,
		{ rovoActivationId },
		{ fetchPolicy: 'network-only' },
	).subscribe({
		next: (data) => {
			if (disposed) {
				return;
			}
			const cloudIds = getNonEmptyCloudIds(data.tenantContexts);
			if (cloudIds.length !== 1) {
				onComplete({ error: new Error('Expected one cloud ID for the Rovo activation.') });
				return;
			}
			cache.set(rovoActivationId, cloudIds[0]);
			onComplete({ cloudId: cloudIds[0] });
		},
		error: (error: Error) => {
			if (!disposed) {
				onComplete({ error });
			}
		},
	});

	return {
		dispose: () => {
			disposed = true;
			subscription.unsubscribe();
		},
	};
};
